import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

/**
 * Overview To do (Console V4, v3/overview/todo.ts): one list per person, built and ordered
 * on the server from the feeds Guardian, System Pulse, Oversight, Policy and the go-live
 * queue already read. Super Admin and Operator read Aithora's list; a Facility Admin reads
 * their own building only.
 */
export type TodoGroup = 'now' | 'today' | 'week' | 'month' | 'later';
export type TodoActionKind = 'go' | 'acknowledge' | 'approve' | 'nudge' | 'take' | 'claim' | 'assign' | 'reply' | 'call' | 'done';

export interface TodoRow {
  /** Stable for the same thing across reads: `<source>:<target>`. */
  id: string;
  source: string;
  group: TodoGroup;
  title: string;
  owner: { userId: string | null; name: string } | null;
  /** The owner line as the list prints it ("Nobody has claimed it"). */
  ownerLine: string;
  /** What gets worse if it waits, when that is not a clock. */
  worse: string | null;
  /** Drives the wait clock. */
  openedAt: string | null;
  /** Past this many minutes the row reads LATE. */
  limitMinutes: number | null;
  nextEscalationAt: string | null;
  /** What happens at nextEscalationAt ("Escalates", "Pages Tier 2"). */
  nextEscalationLabel: string | null;
  assignedTo: { userId: string; name: string | null } | null;
  action: { kind: TodoActionKind; label: string; to: string | null; targetId: string | null; facilityId: string | null };
  basis: 'measured' | 'modeled';
  first?: boolean;
}

export interface TodoSoon {
  at: string | null;
  /** 'in' prints "in 4m"; 'date' prints "Oct 8"; 'label' prints `label`. */
  style: 'in' | 'date' | 'label';
  label: string | null;
  what: string;
  to: string | null;
}

export interface TodoStaff {
  userId: string | null;
  name: string;
  role: string;
  /** Residents assigned this shift; null when they are on the schedule with no assignment. */
  residents: number | null;
}

export interface OverviewTodo {
  real: boolean;
  scope: 'fleet' | 'building';
  role: 'super' | 'operator' | 'building';
  facilityId: string | null;
  at: string;
  rows: TodoRow[];
  soon: TodoSoon[];
  /** Facility Admin only: who has what this shift. */
  staff: TodoStaff[] | null;
  /** Residents nobody is assigned this shift (null when nobody is on shift). */
  unassigned: number | null;
  /** Sources that failed to answer: their rows are missing, not clear. */
  unavailable: string[];
  note: string;
}

export interface TodoActionBody {
  /** The client's own uuid for this tap: a repeat is written once. */
  id: string;
  rowId: string;
  action: 'acknowledge' | 'assign' | 'done' | 'call';
  targetId?: string | null;
  label: string;
  assignTo?: string;
}

export interface TodoLedgerEntry {
  id: string;
  actor: string;
  role: string;
  action: string;
  targetId: string | null;
  rowId: string;
  what: string;
  at: string | null;
}

export interface TodoActionResponse {
  real: boolean;
  duplicate: boolean;
  entry: TodoLedgerEntry;
}

export interface TodoDoneRow {
  id: string;
  at: string;
  what: string;
  by: string;
  action: string;
  targetId: string | null;
}

export interface TodoDone {
  real: boolean;
  since?: string;
  rows: TodoDoneRow[];
}

export const overviewTodo = route<OverviewTodo>()('GET', `${A}/overview/todo`, { honest: true });
export const overviewTodoAction = route<TodoActionResponse, void, TodoActionBody>()('POST', `${A}/overview/todo/actions`, { honest: true });
export const overviewTodoDone = route<TodoDone, { since?: string }>()('GET', `${A}/overview/todo/done`, { honest: true });

export const overviewTodoApi = { overviewTodo, overviewTodoAction, overviewTodoDone } as const;
